<template>
  <div class="task-filters">
    <div class="filters-container">
      <!-- Combined Task Filter -->
      <div class="filter-group">
        <multiselect
          v-model="selectedTaskFilter"
          :options="combinedFilterOptions"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :preserve-search="false"
          :show-labels="false"
          placeholder="Filter tasks..."
          class="filter-select"
        >
          <template #option="{ option }">
            <div class="combined-filter-option">
              <i :class="getCombinedFilterIcon(option)"></i>
              {{ option }}
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Patient Filter -->
      <div class="filter-group">
        <multiselect
          v-model="selectedPatients"
          :options="patientOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :limit="2"
          :limit-text="count => `+${count}`"
          placeholder="Filter by patient"
          label="name"
          track-by="id"
          class="filter-select"
        >
          <template #option="{ option }">
            <div class="patient-option">
              <i class="fa fa-user-circle"></i>
              {{ option.name }}
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Assignee Filter -->
      <div class="filter-group">
        <multiselect
          v-model="selectedAssignees"
          :options="assigneeOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :limit="2"
          :limit-text="count => `+${count}`"
          placeholder="Filter by assignee"
          track-by="id"
          label="name"
          class="filter-select"
        >
          <template #option="{ option }">
            <div class="assignee-option">
              <div
                class="assignee-avatar"
                :style="option.avatar ? `background-image: url(${option.avatar})` : ''"
                :class="getAvatarClass(option.id)"
              >
                {{ !option.avatar ? getInitials(option.name) : '' }}
              </div>
              {{ option.name }}
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Creator Filter -->
      <div class="filter-group">
        <multiselect
          v-model="selectedCreators"
          :options="assigneeOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :limit="2"
          :limit-text="count => `+${count}`"
          placeholder="Filter by creator"
          track-by="id"
          label="name"
          class="filter-select"
        >
          <template #option="{ option }">
            <div class="assignee-option">
              <div
                class="assignee-avatar"
                :style="option.avatar ? `background-image: url(${option.avatar})` : ''"
                :class="getAvatarClass(option.id)"
              >
                {{ !option.avatar ? getInitials(option.name) : '' }}
              </div>
              {{ option.name }}
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Due Date Filter -->
      <div class="filter-group datepicker-filter-group">
        <DatePicker
          lang="en"
          v-model="selectedDueDate"
          :format="dateFormatStr"
          input-class="filter-date-input"
          placeholder="Filter by Due Date"
          append-to-body
        >
        </DatePicker>
      </div>

      <!-- Tags Filter (Include) -->
      <div class="filter-group">
        <multiselect
          v-model="selectedTags"
          :options="tagOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :limit="2"
          :limit-text="count => `+${count}`"
          placeholder="Include tags..."
          class="filter-select"
        >
          <template #option="{ option }">
            <div class="tag-option">
              <i class="fa fa-tag" style="color: #10b981;"></i>
              {{ option }}
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Tags Filter (Exclude) -->
      <div class="filter-group">
        <multiselect
          v-model="selectedExcludeTags"
          :options="tagOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :limit="2"
          :limit-text="count => `+${count}`"
          placeholder="Exclude tags..."
          class="filter-select exclude-tags-select"
        >
          <template #option="{ option }">
            <div class="tag-option">
              <i class="fa fa-ban" style="color: #ef4444;"></i>
              {{ option }}
            </div>
          </template>
        </multiselect>
      </div>

      <!-- Clear Filters Button -->
      <button
        class="clear-filters"
        @click="clearFilters"
      >
        <i class="fa fa-times"></i>
        Clear Filters
      </button>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import NonEscapedHighlight from './NonEscapedHighlight';
import DatePicker from "vue2-datepicker"; // Import vue2-datepicker
// Removed CSS import 'vue2-datepicker/index.css';

export default {
  name: 'TaskFilters',
  components: {
    Multiselect,
    NonEscapedHighlight,
    DatePicker // Register DatePicker
  },
  props: {
    patients: {
      type: Array,
      default: () => []
    },
    employees: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    },
    searchClient: {
      type: Object,
      required: false
    },
    siteID: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      selectedTaskFilter: 'Show all tasks',
      selectedPatients: [],
      selectedAssignees: [],
      selectedCreators: [],
      selectedTags: [],
      selectedExcludeTags: [], // Tags to exclude from results
      selectedDueDate: null,
      searchValue: '',
      dateFormatStr: 'MM/DD/YYYY', // Add date format string
      // formatDateForDisplay method is no longer needed for vue2-datepicker's :format prop
      combinedFilterOptions: [
        'Show all tasks',
        'Show incomplete tasks only',
        'Show overdue tasks only',
        'Show tasks with new comments',
        'Show repeating tasks only',
        'Show non-repeating tasks only',
        'Show all completed tasks', // Added new option
        'Delete all completed tasks'
      ],
      personAvatars: {
        'c7ee40b0-d86d-11e9-bac0-0ad6daef1536': 'https://randomuser.me/api/portraits/women/32.jpg',
        '77e0403d-7c06-4ae6-80d8-10e85245b454': 'https://randomuser.me/api/portraits/women/44.jpg',
        '4f404245-bdd1-478d-aefd-a3118cd2b5dc': 'https://randomuser.me/api/portraits/women/68.jpg'
      }
    };
  },
  methods: {
    getInitials(name) {
      if (!name) return '';
      const parts = name.split(' ');
      if (parts.length === 1) return parts[0].charAt(0);
      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    },
    getAvatarClass(personId) {
      if (this.personAvatars[personId]) {
        return '';
      }
      const colorMap = {
        'c7ee40b0-d86d-11e9-bac0-0ad6daef1536': 'avatar-blue',
        '77e0403d-7c06-4ae6-80d8-10e85245b454': 'avatar-purple',
        '4f404245-bdd1-478d-aefd-a3118cd2b5dc': 'avatar-pink'
      };
      return colorMap[personId] || 'bg-gray-200 text-gray-700';
    },
    getCombinedFilterIcon(option) {
      switch(option) {
        case 'Show all tasks':
          return 'fa fa-list';
        case 'Show incomplete tasks only':
          return 'fa fa-clock-o';
        case 'Show overdue tasks only':
          return 'fa fa-exclamation-circle';
        case 'Show tasks with new comments':
          return 'fa fa-comment';
        case 'Show repeating tasks only':
          return 'fa fa-refresh';
        case 'Show non-repeating tasks only':
          return 'fa fa-ban';
        case 'Delete all completed tasks':
          return 'fa fa-trash';
        case 'Show all completed tasks': // Added icon for new option
          return 'fa fa-check-square-o';
        default:
          return 'fa fa-filter';
      }
    },
    clearFilters() {
      console.log('TaskFilters - clearFilters called');
      this.selectedTaskFilter = 'Show all tasks';
      this.selectedPatients = [];
      this.selectedAssignees = [];
      this.selectedCreators = [];
      this.selectedTags = [];
      this.selectedExcludeTags = [];
      this.selectedDueDate = null;
      this.searchValue = '';

      // Emit the filter-change event with empty filters
      this.$emit('filter-change', {
        taskFilter: 'Show all tasks',
        patients: [],
        assignees: [],
        creators: [],
        tags: [],
        excludeTags: [],
        dueDate: null,
      });

      console.log('TaskFilters - filters cleared');
    },
    emitFilterChange() {
      this.$emit('filter-change', {
        taskFilter: this.selectedTaskFilter,
        patients: this.selectedPatients,
        assignees: this.selectedAssignees,
        creators: this.selectedCreators,
        tags: this.selectedTags,
        excludeTags: this.selectedExcludeTags,
        dueDate: this.selectedDueDate,
      });
    },
    handleTaskClick(taskID) {
      this.$emit('task-click', taskID);
    }
  },
  computed: {
    patientOptions() {
      return this.patients.map(patient => ({
        id: patient.PatNum,
        name: patient.Patient
      }));
    },
    assigneeOptions() {
      return this.employees.map(employee => ({
        id: employee.EmployeeID,
        name: employee.Name,
        avatar: employee.PictureUrl
      }));
    },
    tagOptions() {
      return this.tags || [];
    },
    hasActiveFilters() {
      return this.selectedTaskFilter !== 'Show all tasks' ||
             this.selectedPatients.length > 0 ||
             this.selectedAssignees.length > 0 ||
             this.selectedCreators.length > 0 ||
             this.selectedTags.length > 0 ||
             this.selectedExcludeTags.length > 0 ||
             this.selectedDueDate !== null;
    }
  },
  mounted() {
    // Emit initial filter-change event
    this.emitFilterChange();
  },
  watch: {
    selectedPatients: {
      handler(newVal) {
        console.log('TaskFilters - selectedPatients changed:', JSON.stringify(newVal));
        this.emitFilterChange();
      },
      deep: true
    },
    selectedAssignees: {
      handler(newVal) {
        console.log('TaskFilters - selectedAssignees changed:', JSON.stringify(newVal));
        this.emitFilterChange();
      },
      deep: true
    },
    selectedCreators: {
      handler(newVal) {
        console.log('TaskFilters - selectedCreators changed:', JSON.stringify(newVal));
        this.emitFilterChange();
      },
      deep: true
    },
    selectedTags: {
      handler(newVal) {
        console.log('TaskFilters - selectedTags changed:', JSON.stringify(newVal));
        this.emitFilterChange();
      },
      deep: true
    },
    selectedExcludeTags: {
      handler(newVal) {
        console.log('TaskFilters - selectedExcludeTags changed:', JSON.stringify(newVal));
        this.emitFilterChange();
      },
      deep: true
    },
    selectedDueDate: {
      handler(newVal) {
        // Emit filter-change immediately when due date changes
        this.emitFilterChange();
      },
      deep: false
    },
    selectedTaskFilter: {
      handler(newVal) {
        console.log('TaskFilters - selectedTaskFilter changed:', newVal);
        this.emitFilterChange();
      }
    },
    searchValue(newValue) {
      console.log('TaskFilters - searchValue changed:', newValue);
      this.$emit('search', newValue);
    }
  }
};
</script>
<style scoped>
/* Make assignee tags wider and improve visibility */
.multiselect__tag.assignee-tag {
  min-width: 160px;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  padding-right: 8px;
  font-size: 1rem;
}
/* Also widen the selected value in the multiselect input itself */
.multiselect__single {
  min-width: 120px;
  max-width: 320px;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Match date filter style to other filters */
.date-filter-input {
 min-height: 38px;
 border: 1px solid #ced4da;
 border-radius: 4px;
 padding: 5px 12px;
 background: white;
 font-size: 1rem;
 width: 100%;
 box-sizing: border-box;
}
</style>

<style lang="scss" scoped>
/* Removed DatePicker alignment rule */

.task-filters {
  
  background-color: transparent;
  border-radius: 8px;
  margin-bottom: 1rem;

  .filters-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: flex-end;
  }

  .filter-group {
    flex: 1 1 170px;
    min-width: 140px;
    max-width: 250px;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: flex-end;

    // Ensure datepicker filter aligns with multiselect filters
    &.datepicker-filter-group {
      align-self: flex-end;
      width: 100%;

      // Ensure the datepicker takes full width of its container
      :deep(.mx-datepicker) {
        width: 100%;
      }
    }
  }

  .filter-select {
    width: 100%;
  }

  // Exclude tags dropdown styling
  .exclude-tags-select {
    :deep(.multiselect__tags) {
      border-color: #fecaca;
      background-color: #fef2f2;
    }
    :deep(.multiselect__tag) {
      background-color: #fee2e2;
      color: #dc2626;
    }
  }

  .patient-option,
  .assignee-option,
  .tag-option,
  .combined-filter-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.15rem 0;

    i {
      color: #64748b;
      font-size: 0.875rem;
    }
  }

  .assignee-option {
    .assignee-avatar {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.6rem;
      font-weight: 600;
      color: white;
      background-size: cover;
      background-position: center;
      border: 1.5px solid white;
    }
  }

  .clear-filters {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border: none;
    border-radius: 6px;
    background-color: #f1f5f9;
    color: #64748b;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 34px;
    margin-bottom: 0.25rem;
    align-self: flex-end;

    &:hover:not(:disabled) {
      background-color: #e2e8f0;
      color: #475569;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i {
      font-size: 0.8125rem;
    }
  }
}

// Date picker styling - matching multiselect height exactly
:deep(.mx-datepicker) {
  width: 100%;
  height: 34px !important;
  min-height: 34px !important;
  max-height: 34px !important;

  .mx-input-wrapper {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    height: 34px !important;
    min-height: 34px !important;
    max-height: 34px !important;
    background-color: white;
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;

    &:focus-within {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 1px #3b82f6;
    }
  }

  // Target the actual input element
  input.mx-input,
  .filter-date-input {
    border: none !important;
    background: transparent !important;
    padding: 4px 8px !important;
    font-size: 0.8125rem !important;
    color: #64748b !important;
    flex: 1;
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    line-height: 32px !important;
    box-sizing: border-box !important;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }
  }

  .mx-input-append {
    padding: 4px 6px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: auto;
    height: 32px;

    .mx-calendar-icon {
      width: 14px;
      height: 14px;
      fill: #64748b;
    }

    .mx-clear-icon {
      color: #9ca3af;
      font-size: 0.75rem;
      cursor: pointer;
      padding: 2px;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        color: #ef4444;
        background-color: #fef2f2;
      }
    }
  }
}

// Override multiselect styles to match our design
:deep(.multiselect) {
  height: 34px;
  
  .multiselect__tags {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 4px 8px;
    height: 34px;
    background-color: white;
    font-size: 0.8125rem;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    box-sizing: border-box;
    overflow: hidden;
  }

  .multiselect__tag {
    background-color: #e5e7eb;
    color: #475569;
    border-radius: 4px;
    padding: 0.15rem 0.4rem 0.15rem 0.5rem;
    margin: 0.15rem;
    font-size: 0.75rem;
    font-weight: 500;
    height: 22px;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    max-width: 75px;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;

    span {
      max-width: 50px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      vertical-align: middle;
    }

    .multiselect__tag-icon {
      line-height: 22px;
      background-color: #d1d5db;
      border-radius: 3px;
      width: 19px;
      height: 19px;
      min-width: 19px;
      margin-left: 5px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: #9ca3af;
        color: #ffffff;
      }

      &:after {
        color: #4b5563;
        font-size: 12px;
        font-weight: 600;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  .multiselect--active {
    .multiselect__tags {
      border-color: #cbd5e1;
    }
  }

  .multiselect__placeholder {
    margin-bottom: 2px;
    padding-top: 0;
    padding-left: 0;
    font-size: 0.8125rem;
  }

  .multiselect__select {
    height: 32px;
    width: 28px;
    top: 1px;
    padding: 2px 0 0 0;
    z-index: 1;

    &:before {
      border-color: #64748b transparent transparent;
      border-width: 5px 5px 0;
      top: 45%;
    }

    &.is-open:before {
      transform: rotate(180deg);
    }
  }

  .multiselect__input,
  .multiselect__single {
    font-size: 0.8125rem;
    line-height: 1.2;
    padding: 2px 0;
    margin-bottom: 0;
  }

  .multiselect__content-wrapper {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    margin-top: 2px;
  }

  .multiselect__element {
    margin: 0;
    padding: 0;
  }

  .multiselect__option {
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    color: #1e293b;
    min-height: 28px;
    line-height: 1.2;

    &--highlight {
      background-color: #f1f5f9;
    }

    &--selected {
      background-color: #eef2ff;
      color: #4f46e5;
      font-weight: 500;
    }
  }

  .multiselect__input {
    padding: 0;
    font-size: 0.8125rem;
    color: #1e293b;
  }
}

// Removed margin-bottom that was causing misalignment
.mx-datepicker {
  margin-bottom: 0 !important;
}

.task-filters input {
  border: none;
}

.mx-input-append {
  position: relative;
  padding: 2px;
}

// Responsive styles

/* Large tablet / small desktop (769px - 1024px) */
@media (min-width: 769px) and (max-width: 1024px) {
  .task-filters {
    margin-bottom: 0.5rem;

    .filters-container {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.5rem;
    }

    .filter-group {
      min-width: 0;
      max-width: 100%;
      margin-bottom: 0;
    }

    .clear-filters {
      grid-column: 1 / -1; /* Span full width */
      justify-content: center;
      height: 32px;
      padding: 0.3rem 0.6rem;
      font-size: 0.75rem;
    }

    :deep(.multiselect) {
      height: 32px;
      font-size: 0.8125rem;
    }

    :deep(.multiselect__tags) {
      height: 32px;
      padding: 3px 6px;
    }
  }
}

/* Mobile / small tablet (481px - 768px) */
@media (min-width: 481px) and (max-width: 768px) {
  .task-filters {
    margin-bottom: 0.5rem;

    .filters-container {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.4rem;
    }

    .filter-group {
      min-width: 0;
      max-width: 100%;
      margin-bottom: 0;
    }

    .clear-filters {
      grid-column: 1 / -1;
      justify-content: center;
      height: 32px;
      font-size: 0.75rem;
    }

    :deep(.multiselect) {
      height: 32px;
    }

    :deep(.multiselect__tags) {
      height: 32px;
      padding: 3px 6px;
    }
  }
}

/* Phone styles (<= 480px) */
@media (max-width: 480px) {
  .task-filters {
    margin-bottom: 0.375rem;

    .filters-container {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    .filter-group {
      flex: 1 1 100%;
      max-width: 100%;
      min-width: 0;
      width: 100%;
      margin-bottom: 0;
    }

    .clear-filters {
      width: 100%;
      justify-content: center;
      margin-top: 0.25rem;
      height: 30px;
      font-size: 0.75rem;
    }

    :deep(.multiselect) {
      height: 32px;
    }

    :deep(.multiselect__tags) {
      height: 32px;
      padding: 3px 6px;
      font-size: 0.75rem;
    }

    :deep(.multiselect__placeholder) {
      font-size: 0.75rem;
    }
  }
}
</style>
